import * as constants from './constants';

export interface SessionTokenResponse {
  token: string;
}
export const fetchSessionToken = async (signal: AbortSignal): Promise<SessionTokenResponse> => {
  console.log('Attempting to get session token');
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username: process.env[`REACT_APP_SESSION_ID`],
      password: process.env[`REACT_APP_SESSION_CODE`],
    }),
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface LoanInfoResponse {
  b1FirstName: string;
  b1MiddleName: string;
  b1LastName: string;
  upfrontMIPFinanced: number;
  upFrontPMIFinanced: number;
  miCompany: string;
  miCertificate: string;
  agencyCaseNumber: string;
  closingDate: number;
  amortizationTerm: number;
  ltv: number;
  loanConventional: boolean;
  loanConventionalJumbo: boolean;
  loanFha: boolean;
  loanUsda: boolean;
}
export const fetchLoanData = async (
  signal: AbortSignal,
  loanNo: string,
  sessionToken: string,
): Promise<LoanInfoResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/loan/${loanNo}`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface CheckRoutingResponse {
  valid: boolean;
  description: string;
}

export const checkRoutingInfo = async (
  loanNo: string,
  routingNo: string,
  accountNo: string,
  sessionToken: string,
): Promise<CheckRoutingResponse> => {
  if (!loanNo || !routingNo || !accountNo || !sessionToken) return null;
  const response: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}live/servicing/loan/${loanNo}/validRouting/${routingNo}/${accountNo}`,
    {
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return await response.json();
};

export interface ModelAccessResponse {
  fullAccess: boolean;
  readAccess: boolean;
}

export const modelAccessInfo = async (
  model: string,
  servicerUser: boolean,
  sessionToken: string,
): Promise<ModelAccessResponse> => {
  if (!servicerUser || !model) return null;
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/security/access-level/${model}`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return await response.json();
};

export interface ImpoundListResponse {
  firstPmntDate: number;
  impoundType: string;
  impoundGroup: string;
  monthsReqired: number;
  impoundEscrowed: boolean;
}
export const fetchImpoundListData = async (
  signal: AbortSignal,
  loanNo: string,
  sessionToken: string,
): Promise<ImpoundListResponse[]> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}/live/loan/${loanNo}/impound/list`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface ServicingArrayEntry {
  payAgent: boolean;
  unitNo: string;
  agentCompany: string;
  servArrComment: string;
  policyNo: string;
  servArrAmountPaid: number;
  servArrSupAmountPaid: number;
  policyStatus: string;
  companyToPay: string;
  servArrPayeeNumber: string;
  servArrCancelDate: number;
  servArrSupAmountDue: number;
  servArrAmountDue: number;
  servArrCoverage: number;
  $index: number;
}
export interface Parcel {
  amountDue: number;
  parcelNo: string;
  amountPaid: number;
  assessorCode: string;
  supAmountDue: number;
  parcelStatus: string;
  supAmountPaid: number;
  parcelArrComment: string;
  $index: number;
}
export interface ImpoundPolicyListResponse {
  linkedToImp1: boolean;
  parcels: Array<Parcel>;
  insuranceComp: string;
  payeeNumber: string;
  impoundType: string;
  renewalDte: number;
  comment: string;
  servPremium: number;
  servSupPremium: number;
  servAmtPaid: number;
  servSupAmtPaid: number;
  policyNum: string;
  acAgentCompany: string;
  acAgentPhone: string;
  acAgentPhoneExt: string;
  coverageAmt: number;
  cancelDte: number;
  cancelationDte: number;
  escrowed: boolean;
  activeStatus: boolean;
  agentCompanyCode: string;
  servicingArray: Array<ServicingArrayEntry>;
}
export const fetchImpoundPolicyListData = async (
  signal: AbortSignal,
  sessionToken: string,
  loanNo: string,
  impoundGroup: string,
  impoundType: string,
): Promise<ImpoundPolicyListResponse[]> => {
  const response: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}/live/loan/${loanNo}/impound/${impoundGroup}/${impoundType}/renewalDate/list`,
    {
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      signal,
    },
  );
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface SubjectPropertyResponse {
  state: string;
}
export const fetchSubjectPropertyInfo = async (
  signal: AbortSignal,
  sessionToken: string,
  loanNo: string,
): Promise<SubjectPropertyResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/loan/${loanNo}/property/001`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface QIDbyKeywordResponse {
  code: string;
}
export const fetchQIDByKeyword = async (
  signal: AbortSignal,
  sessionToken: string,
  qid: string,
  keyword: string,
): Promise<QIDbyKeywordResponse> => {
  const response: { [key: string]: any } = await fetch(
    `${process.env['REACT_APP_REST_BASE']}live/master/qid/${qid}/keyword/${keyword}`,
    {
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      signal,
    },
  );
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface TaxPayeeResponse {
  assessorCode: string;
  nameOne: string;
  phone: string;
  phoneExt: string;
}
export const fetchTaxPayeeInfo = async (
  signal: AbortSignal,
  sessionToken: string,
  assessorCode: string,
): Promise<TaxPayeeResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/master/assessor/${assessorCode}`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface InsCompResponse {
  compCode: string;
  name1: string;
  primaryContactPhone: string;
  primaryContactPhoneExtension: string;
}
export const fetchInsCompInfo = async (
  signal: AbortSignal,
  sessionToken: string,
  compCode: string,
): Promise<InsCompResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/master/company/${compCode}`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface MiscPayeeInfoResponse {
  payeeNumber: string;
  nameOne: string;
  phoneNo: string;
}
export const fetchMiscPayeeInfo = async (
  signal: AbortSignal,
  sessionToken: string,
  payeeNumber: string,
): Promise<MiscPayeeInfoResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/master/miscpayee/${payeeNumber}`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface QIDListResponse {
  code: string;
  description: string;
}
export const fetchQIDList = async (
  signal: AbortSignal,
  sessionToken: string,
  qid: string,
): Promise<QIDListResponse[]> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}/live/master/qid/${qid}/list`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface CompCodeResponse {
  companyCode: string;
  name: string;
  name2: string;
  phoneNumber: string;
  faxNumber: string;
  phoneExt: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  contactEmail: string;
}
export const fetchCompCodes = async (
  signal: AbortSignal,
  sessionToken: string,
  busTypeCodes: Array<string>,
): Promise<CompCodeResponse[]> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/master/company/search`, {
    method: 'POST',
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ addressType: '01', businessTypes: busTypeCodes }),
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface TaxAssessorsResponse {
  assessorCode: string;
  nameOne: string;
  addr1: string;
  city: string;
  state: string;
  zip: string;
  checkName: string;
  suppCheckName: string;
}
export const fetchTaxAssessors = async (
  signal: AbortSignal,
  sessionToken: string,
  stateCode: string,
  impoundType: string,
): Promise<TaxAssessorsResponse[]> => {
  const response: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}live/master/assessor/stateCde/${stateCode}/taxType/${impoundType}/tax/list`,
    {
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      signal,
    },
  );
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

export interface MiscPayeeResponseElement {
  payeeNumber: string;
  nameOne: string;
  nameTwo: string;
  addrOne: string;
  addrTwo: string;
  city: string;
  state: string;
  zip: string;
  emailAddr: string;
  phoneNo: string;
  payeeTin: string;
}
export interface MiscPayeeResponse {
  payees: MiscPayeeResponseElement[];
}
export const fetchMiscPayees = async (signal: AbortSignal, sessionToken: string): Promise<MiscPayeeResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/servicing/miscPayees/search`, {
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  return response.json();
};

interface PreSendData {
  active: boolean;
  activeStatus: boolean;
}
export interface SubmitImpoundResponse {
  preliminaryData: { [key: string]: any };
  responseData: { [key: string]: any };
}
export const submitImpoundInfo = async (
  signal: AbortSignal,
  sessionToken: string,
  loanNo: string,
  impoundGroup: string,
  impoundType: string,
  sendData: { [key: string]: any },
  renewalDte: string,
  imp1EntryExists: boolean,
): Promise<SubmitImpoundResponse> => {
  const preSendData: PreSendData = {
    active: true,
    activeStatus: true,
  };
  let preliminaryData: { [key: string]: any };
  if (!imp1EntryExists) {
    const preliminaryResponse: Response = await fetch(
      `${process.env['REACT_APP_REST_BASE']}live/loan/${loanNo}/impound/${impoundGroup}/${impoundType}`,
      {
        method: 'POST',
        headers: {
          'Session-Token': `${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preSendData),
        signal,
      },
    );
    if (!preliminaryResponse.ok) {
      const errorData: { [key: string]: any } = await preliminaryResponse.json();
      console.log(errorData);
      const errMessage: string = 'Error: ' + errorData.error;
      throw new Error(errMessage);
    }
    preliminaryData = await preliminaryResponse.json();
  }

  const response: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}live/loan/${loanNo}/impound/${impoundGroup}/${impoundType}/renewalDate/${renewalDte}`,
    {
      method: 'POST',
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(sendData),
      signal,
    },
  );
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const responseData: { [key: string]: any } = await response.json();

  return { preliminaryData, responseData };
};

// export interface AddCompanyResponse{
//   responseData: { [key: string]: any };
// }

export interface EditCompanyInfoResponse {
  mainResponseData: { [key: string]: any };
  addrResponseData: { [key: string]: any };
}
export const editCompanyInfo = async (
  signal: AbortSignal,
  sessionToken: string,
  companyCode: string,
  addressType: string,
  mainSendData: { [key: string]: boolean | string },
  addrSendData: { [key: string]: boolean | string },
): Promise<EditCompanyInfoResponse> => {
  const mainResponse: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}live/master/company/${companyCode}`,
    {
      method: 'POST',
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mainSendData),
      signal,
    },
  );
  if (!mainResponse.ok) {
    const errorData: { [key: string]: any } = await mainResponse.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const mainResponseData: { [key: string]: any } = await mainResponse.json();

  const addrResponse: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}live/master/company/${companyCode}/address/${addressType}`,
    {
      method: 'POST',
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(addrSendData),
      signal,
    },
  );
  if (!addrResponse.ok) {
    const errorData: { [key: string]: any } = await addrResponse.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const addrResponseData: { [key: string]: any } = await addrResponse.json();

  return { mainResponseData, addrResponseData };
};

export interface AddCompanyResponse {
  mainResponseData: { [key: string]: any };
  addrResponseData: { [key: string]: any };
}
export const addCompany = async (
  signal: AbortSignal,
  sessionToken: string,
  mainSendData: { [key: string]: boolean | string },
  addrSendData: { [key: string]: boolean | string },
): Promise<AddCompanyResponse> => {
  const mainResponse: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/master/company/add`, {
    method: 'POST',
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mainSendData),
    signal,
  });
  if (!mainResponse.ok) {
    const errorData: { [key: string]: any } = await mainResponse.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const mainResponseData: { [key: string]: any } = await mainResponse.json();
  const companyCode: string = mainResponseData.compCode;

  const addrResponse: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}live/master/company/${companyCode}/address/${constants.primaryAddress}`,
    {
      method: 'POST',
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(addrSendData),
      signal,
    },
  );
  if (!addrResponse.ok) {
    const errorData: { [key: string]: any } = await addrResponse.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const addrResponseData: { [key: string]: any } = await addrResponse.json();

  return { mainResponseData, addrResponseData };
};

export interface EditTaxAssessorInfoResponse {
  responseData: { [key: string]: any };
}
export const editTaxAssessorInfo = async (
  signal: AbortSignal,
  sessionToken: string,
  assessorCode: string,
  sendData: { [key: string]: boolean | string },
): Promise<EditTaxAssessorInfoResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/master/assessor/${assessorCode}`, {
    method: 'POST',
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sendData),
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const responseData: { [key: string]: any } = await response.json();
  return { responseData };
};

export interface EditMiscPayeeInfoResponse {
  responseData: { [key: string]: any };
}
export const editMiscPayeeInfo = async (
  signal: AbortSignal,
  sessionToken: string,
  payeeNumber: string,
  sendData: { [key: string]: boolean | string },
): Promise<EditMiscPayeeInfoResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/master/miscpayee/${payeeNumber}`, {
    method: 'POST',
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sendData),
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const responseData: { [key: string]: any } = await response.json();
  return { responseData };
};

export interface AddMiscPayeeResponse {
  responseData: { [key: string]: any };
}
export const addMiscPayee = async (
  signal: AbortSignal,
  sessionToken: string,
  sendData: { [key: string]: boolean | string },
): Promise<EditMiscPayeeInfoResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/master/miscpayee/add`, {
    method: 'POST',
    headers: {
      'Session-Token': `${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sendData),
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const responseData: { [key: string]: any } = await response.json();
  return { responseData };
};

export interface PmiServicingResponse {
  responseData: { [key: string]: any };
}
export const pmiServicing = async (
  signal: AbortSignal,
  sessionToken: string,
  opType: string,
  pmiCompany: string,
  isTest: string, //SQC-490 Jacques Bega, query parameter for denoting whether payment files are for test or production, ignored by billing operations
): Promise<PmiServicingResponse> => {
  const response: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}live/servicing/pmi/${opType}/${pmiCompany}?isTest=${isTest}`,
    {
      method: 'POST',
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      signal,
    },
  );
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const responseData: { [key: string]: any } = await response.json();
  return { responseData };
};

//SQC-647 Jacques Bega
//adding request response structure for MIP servicing endpoint
export interface MipServicingResponse {
  responseData: { [key: string]: any };
}
export const mipServicing = async (
  signal: AbortSignal,
  sessionToken: string,
  opType: string,
  formData: FormData,
): Promise<MipServicingResponse> => {
  const response: Response = await fetch(`${process.env['REACT_APP_REST_BASE']}live/servicing/mip/${opType}`, {
    method: 'POST',
    headers: {
      'Session-Token': `${sessionToken}`,
      //'Content-Type': 'application/json',
    },
    body: formData,
    signal,
  });
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    console.log(errorData);
    const errMessage: string = 'Error: ' + errorData.error;
    throw new Error(errMessage);
  }
  const responseData: { [key: string]: any } = await response.json();
  return { responseData };
};

// SQC-562 / API-1303: MidFirst Bank Servicing Transfer — teman.beck
export interface MidFirstTransferResponse {
  success: boolean;
  zipFileName: string;
  filesGenerated: number;
}
export const generateMidFirstTransfer = async (
  signal: AbortSignal,
  sessionToken: string,
  data: { transfer_date: string; loan_numbers: string[]; loan_type: string; upload_to_imaging: boolean },
): Promise<MidFirstTransferResponse> => {
  const response: Response = await fetch(
    `${process.env['REACT_APP_REST_BASE']}docprinter/servicing/investor/midfirst/transfer`,
    {
      method: 'POST',
      headers: {
        'Session-Token': `${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      signal,
    },
  );
  if (!response.ok) {
    const errorData: { [key: string]: any } = await response.json();
    const errMessage: string = 'Error: ' + (errorData.error || response.statusText);
    throw new Error(errMessage);
  }

  // Response is a zip blob — trigger browser download
  const blob: Blob = await response.blob();
  const contentDisposition: string = response.headers.get('Content-Disposition') || '';
  const match: RegExpMatchArray | null = contentDisposition.match(/filename="?([^"]+)"?/);
  const zipFileName: string = match ? match[1] : `MidFirst_Transfer_${data.transfer_date}.zip`;
  const filesGenerated: number = parseInt(response.headers.get('X-Files-Generated') || '0', 10);

  const url: string = URL.createObjectURL(blob);
  const a: HTMLAnchorElement = document.createElement('a');
  a.href = url;
  a.download = zipFileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return { success: true, zipFileName, filesGenerated };
};
